/**
 * Enable/trigger/delete for a schedule row, with the orchestrator's error
 * contract handled in one place.
 *
 * Lifted out of PipelineSchedulesList so the table and the timeline can drive
 * the same actions. The HTTP status branches are carried over unchanged — they
 * are the compatibility surface with an external service:
 *
 *   429  too soon after the last run   → cooldown, `retry_after_seconds`
 *   409  a run is already in flight    → different cause, different message
 *   410  the schedule is gone          → refetch, it was deleted elsewhere
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@vehix/shared/admin/portal-bridge';
import {
  useUpdatePipelineSchedule,
  useDeletePipelineSchedule,
  useTriggerPipelineSchedule,
  pipelineKeys
} from './usePipelineOrchestrator';
import type { PipelineSchedule } from '../services/pipelineOrchestrator';
import { PipelineApiError } from '../services/pipelineErrors';

export interface ScheduleRowActions {
  toggleEnabled: (schedule: PipelineSchedule) => void;
  triggerNow: (schedule: PipelineSchedule) => void;
  /** Triggers several, reporting one summary instead of a toast per schedule. */
  triggerMany: (schedules: PipelineSchedule[]) => Promise<void>;
  setEnabledMany: (schedules: PipelineSchedule[], enabled: boolean) => Promise<void>;
  /** Writes one recipient list across several schedules, reporting partial failure. */
  setRecipientsForMany: (schedules: PipelineSchedule[], emails: string[]) => Promise<void>;
  remove: (schedule: PipelineSchedule) => void;
  isCoolingDown: (id: string) => boolean;
}

/** Why a trigger did or didn't start a run. Mirrors the orchestrator's statuses. */
type TriggerOutcome =
  | { kind: 'ok'; schedule: PipelineSchedule }
  | { kind: 'cooldown'; schedule: PipelineSchedule; seconds: number }
  | { kind: 'in_flight'; schedule: PipelineSchedule }
  | { kind: 'error'; schedule: PipelineSchedule; err: unknown };

type ToastFn = ReturnType<typeof useToast>['toast'];

/** The single-schedule failure message, kept in one place so both paths agree. */
function reportTriggerFailure(
  outcome: Exclude<TriggerOutcome, { kind: 'ok' }>,
  toast: ToastFn,
  handleApiError: (err: unknown, fallback: string) => void
): void {
  if (outcome.kind === 'cooldown') {
    toast({
      title: `Try again in ${outcome.seconds}s`,
      description: `Cooldown is ${outcome.schedule.min_interval_minutes} min between runs`,
      variant: 'destructive'
    });
    return;
  }
  if (outcome.kind === 'in_flight') {
    toast({
      title: 'A run is already in flight',
      description: outcome.schedule.schedule_name,
      variant: 'destructive'
    });
    return;
  }
  handleApiError(outcome.err, 'Failed to trigger pipeline');
}

/**
 * One toast for a batch.
 *
 * Says how many started *and* why the rest didn't — "3 started, 2 cooling down"
 * is actionable, whereas a bare "3 started" leaves the operator to work out
 * which two are missing.
 */
function summarizeTriggers(outcomes: TriggerOutcome[], toast: ToastFn): void {
  const started = outcomes.filter(o => o.kind === 'ok').length;
  const cooling = outcomes.filter(o => o.kind === 'cooldown').length;
  const inFlight = outcomes.filter(o => o.kind === 'in_flight').length;
  const failed = outcomes.filter(o => o.kind === 'error').length;

  const notes = [
    cooling > 0 ? `${cooling} cooling down` : null,
    inFlight > 0 ? `${inFlight} already running` : null,
    failed > 0 ? `${failed} failed` : null
  ].filter(Boolean);

  toast({
    title: `${started} of ${outcomes.length} pipeline${outcomes.length === 1 ? '' : 's'} triggered`,
    ...(notes.length > 0 && { description: notes.join(' · ') }),
    ...(started === 0 && { variant: 'destructive' as const })
  });
}

// eslint-disable-next-line max-lines-per-function
export function useScheduleRowActions(): ScheduleRowActions {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [cooldowns, setCooldowns] = useState<Record<string, number>>({});

  const updateSchedule = useUpdatePipelineSchedule();
  const deleteSchedule = useDeletePipelineSchedule();
  const triggerSchedule = useTriggerPipelineSchedule();

  // Cooldowns are cleared by timers; drop any still pending on unmount so a
  // setState can't fire against a torn-down component when the user leaves
  // the tab mid-cooldown.
  const timers = useRef<number[]>([]);
  useEffect(
    () => () => {
      timers.current.forEach(clearTimeout);
      timers.current = [];
    },
    []
  );

  const handleApiError = useCallback(
    (err: unknown, fallbackTitle: string) => {
      if (err instanceof PipelineApiError && err.status === 410) {
        queryClient.invalidateQueries({ queryKey: pipelineKeys.schedules });
        toast({ title: 'Schedule was deleted by another user', variant: 'destructive' });
        return;
      }
      toast({ title: fallbackTitle, variant: 'destructive' });
    },
    [queryClient, toast]
  );

  const toggleEnabled = useCallback(
    (schedule: PipelineSchedule) => {
      toast({ title: schedule.is_enabled ? 'Pausing schedule...' : 'Resuming schedule...' });
      updateSchedule.mutate(
        { id: schedule.id, is_enabled: !schedule.is_enabled },
        { onError: err => handleApiError(err, 'Failed to update schedule') }
      );
    },
    [handleApiError, toast, updateSchedule]
  );

  const remove = useCallback(
    (schedule: PipelineSchedule) => {
      toast({ title: 'Deleting schedule...' });
      deleteSchedule.mutate(schedule.id, {
        onError: err => handleApiError(err, 'Failed to delete schedule')
      });
    },
    [deleteSchedule, handleApiError, toast]
  );

  const startCooldown = useCallback((id: string, seconds: number) => {
    setCooldowns(prev => ({ ...prev, [id]: Date.now() + seconds * 1000 }));
    const timer = window.setTimeout(() => {
      setCooldowns(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }, seconds * 1000);
    timers.current.push(timer);
  }, []);

  /**
   * Fires one trigger and resolves with what happened.
   *
   * Uses `mutateAsync` rather than `mutate` + callbacks deliberately. React
   * Query's MutationObserver keeps a *single* `mutateOptions` slot, so a second
   * `mutate()` before the first settles overwrites the first call's onSuccess
   * and onError. The requests all still fire — but every result except the last
   * is silently dropped, which for a bulk trigger means a cooldown or an
   * already-running conflict disappears without a word. Owning the promise here
   * makes each outcome reachable.
   */
  const runTrigger = useCallback(
    async (schedule: PipelineSchedule): Promise<TriggerOutcome> => {
      try {
        await triggerSchedule.mutateAsync({
          id: schedule.id,
          idempotencyKey: crypto.randomUUID()
        });
        return { kind: 'ok', schedule };
      } catch (err) {
        if (err instanceof PipelineApiError) {
          if (err.status === 429) {
            const seconds = err.problem.retry_after_seconds ?? 60;
            startCooldown(schedule.id, seconds);
            return { kind: 'cooldown', schedule, seconds };
          }
          if (err.status === 409) return { kind: 'in_flight', schedule };
        }
        return { kind: 'error', schedule, err };
      }
    },
    [startCooldown, triggerSchedule]
  );

  const triggerNow = useCallback(
    (schedule: PipelineSchedule) => {
      toast({ title: 'Triggering pipeline...', description: schedule.schedule_name });
      void runTrigger(schedule).then(outcome => {
        if (outcome.kind === 'ok') {
          toast({ title: 'Pipeline triggered', description: 'Check the Runs tab for progress' });
          return;
        }
        reportTriggerFailure(outcome, toast, handleApiError);
      });
    },
    [handleApiError, runTrigger, toast]
  );

  /**
   * Triggers several schedules and reports one summary.
   *
   * Sequential rather than parallel: these are crawl jobs, and firing a dozen
   * POSTs at the orchestrator at once is exactly the burst the per-schedule
   * cooldown exists to discourage. The loop is also what makes the summary
   * counts trustworthy.
   */
  const triggerMany = useCallback(
    async (schedules: PipelineSchedule[]) => {
      if (schedules.length === 0) return;
      toast({
        title: `Triggering ${schedules.length} pipeline${schedules.length === 1 ? '' : 's'}...`
      });

      const outcomes: TriggerOutcome[] = [];
      for (const schedule of schedules) {
        outcomes.push(await runTrigger(schedule));
      }

      summarizeTriggers(outcomes, toast);
    },
    [runTrigger, toast]
  );

  /**
   * Writes one recipient list to several schedules.
   *
   * The orchestrator has no settings endpoint, so a "shared" recipient list is
   * only shared because every schedule holds the same copy. Keeping them in
   * step therefore means N writes, and a partial failure has to be reported —
   * silently leaving three schedules mailing the wrong people is the failure
   * mode worth guarding against.
   */
  const setRecipientsForMany = useCallback(
    async (schedules: PipelineSchedule[], emails: string[]) => {
      if (schedules.length === 0) return;
      toast({
        title: `Updating ${schedules.length} schedule${schedules.length === 1 ? '' : 's'}...`
      });

      const results = await Promise.allSettled(
        schedules.map(s => updateSchedule.mutateAsync({ id: s.id, notification_emails: emails }))
      );
      const failed = results.filter(r => r.status === 'rejected').length;

      if (failed === 0) {
        toast({
          title: `Recipients updated on ${schedules.length} schedule${
            schedules.length === 1 ? '' : 's'
          }`
        });
        return;
      }
      toast({
        title: `${schedules.length - failed} updated, ${failed} failed`,
        description: 'The failed schedules still have their previous recipients.',
        variant: 'destructive'
      });
    },
    [toast, updateSchedule]
  );

  /** Enables or disables several schedules, reporting failures rather than hiding them. */
  const setEnabledMany = useCallback(
    async (schedules: PipelineSchedule[], enabled: boolean) => {
      const targets = schedules.filter(s => s.is_enabled !== enabled);
      if (targets.length === 0) return;

      toast({
        title: `${enabled ? 'Resuming' : 'Pausing'} ${targets.length} schedule${
          targets.length === 1 ? '' : 's'
        }...`
      });

      const results = await Promise.allSettled(
        targets.map(s => updateSchedule.mutateAsync({ id: s.id, is_enabled: enabled }))
      );
      const failed = results.filter(r => r.status === 'rejected').length;

      if (failed === 0) {
        toast({ title: `${targets.length} ${enabled ? 'resumed' : 'paused'}` });
        return;
      }
      toast({
        title: `${targets.length - failed} ${enabled ? 'resumed' : 'paused'}, ${failed} failed`,
        variant: 'destructive'
      });
    },
    [toast, updateSchedule]
  );

  const isCoolingDown = useCallback(
    (id: string): boolean => {
      const until = cooldowns[id];
      return until !== undefined && until > Date.now();
    },
    [cooldowns]
  );

  return {
    toggleEnabled,
    triggerNow,
    triggerMany,
    setEnabledMany,
    setRecipientsForMany,
    remove,
    isCoolingDown
  };
}
